import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, Zap, Percent, Clock, TrendingDown, TrendingUp } from 'lucide-react';
import { parseISO, format } from 'date-fns';
import { ElectricityPrice, PriceArea } from '../types';
import { calculateElectricitySubsidy } from '../utils/subsidyCalculator';
import { nb } from 'date-fns/locale';

interface ElectricityCostListProps {
  currentPrice: number | null;
  priceAfterSubsidy: number | null;
  prices: ElectricityPrice[];
  area?: PriceArea;
}

interface UsageItem {
  id: string;
  name: string;
  icon: string;
  kWhUsage: number;
  duration: string;
  description: string;
}

const usageExamples: UsageItem[] = [
  {
    id: 'ev-charging',
    name: 'Lade elbil',
    icon: '🚗',
    kWhUsage: 50,
    duration: 'Full lading',
    description:
      'Tesla Model 3 fra 20% til 80% batteri. Ca. 50 kWh for en full lading (250-300 km rekkevidde).',
  },
  {
    id: 'washing-machine',
    name: 'Vaske klær',
    icon: '🧺',
    kWhUsage: 1.5,
    duration: 'Per vask (60°C)',
    description:
      'En standard vask på 60°C bruker ca. 1–2 kWh avhengig av maskinens energieffektivitet.',
  },
  {
    id: 'tumble-dryer',
    name: 'Tørketrommel',
    icon: '👕',
    kWhUsage: 3.5,
    duration: 'Per tørk',
    description:
      'Tørketrommelen er strømintensiv, ofte 2.5–4.5 kWh per tørk avhengig av modell og mengde klær.',
  },
  {
    id: 'dishwasher',
    name: 'Oppvaskmaskin',
    icon: '🍽️',
    kWhUsage: 1.3,
    duration: 'Per syklus',
    description:
      'En moderne oppvaskmaskin bruker ca. 1–1.5 kWh per vask. Ecoprogrammer bruker mindre strøm, men tar lengre tid.',
  },
  {
    id: 'refrigerator',
    name: 'Kjøleskap og fryser',
    icon: '❄️',
    kWhUsage: 1.5,
    duration: 'Per døgn',
    description:
      'Et kombinert kjøleskap/fryser kan bruke 1–2 kWh per døgn, avhengig av størrelse, alder og energiklasse.',
  },
  {
    id: 'vacuum-cleaner',
    name: 'Støvsuger',
    icon: '🧹',
    kWhUsage: 0.6,
    duration: '1 time',
    description:
      'En støvsuger på 500–700W forbruker 0.5–0.7 kWh for én times bruk.',
  },
  {
    id: 'heating',
    name: 'Panelovn',
    icon: '🔥',
    kWhUsage: 1.0,
    duration: '1 time',
    description:
      'En panelovn på 1000W forbruker 1 kWh per time når den går på full effekt.',
  },
  {
    id: 'heat-pump',
    name: 'Varmepumpe',
    icon: '♨️',
    kWhUsage: 0.7,
    duration: '1 time',
    description:
      'Varmepumper er mer effektive enn panelovner og bruker typisk 0.5–1 kWh/time, avhengig av temperaturforskjell.',
  },
  {
    id: 'floor-heating',
    name: 'Gulvvarme',
    icon: '🏠',
    kWhUsage: 0.6,
    duration: '1 time (5m²)',
    description:
      'Elektrisk gulvvarme bruker ofte ca. 100–150W/m². Et 5 m² rom kan da bruke 0.5–0.8 kWh/time.',
  },
  {
    id: 'water-heater',
    name: 'Varmtvannsbereder',
    icon: '🚿',
    kWhUsage: 4.0,
    duration: 'Per døgn',
    description:
      'En 200L varmtvannsbereder krever gjerne 3–5 kWh per døgn, avhengig av isolasjon og varmtvannsforbruk.',
  },
  {
    id: 'shower',
    name: 'Dusj',
    icon: '🛁',
    kWhUsage: 2.5,
    duration: '10 minutter',
    description:
      'En 10-minutters dusj kan kreve 50–80L varmtvann, ofte ca. 2–3 kWh for oppvarming.',
  },
  {
    id: 'oven',
    name: 'Stekeovn',
    icon: '🍳',
    kWhUsage: 1.2,
    duration: '1 time',
    description:
      'En stekeovn forbruker gjerne 1–1.5 kWh/t, avhengig av temperatur og ovntype.',
  },
  {
    id: 'induction-hob',
    name: 'Induksjonstopp',
    icon: '♨️',
    kWhUsage: 0.8,
    duration: '30 minutter',
    description:
      'En induksjonstopp på maks effekt (1.5–1.8 kWh/t) bruker rundt 0.8 kWh på 30 min.',
  },
  {
    id: 'microwave',
    name: 'Mikrobølgeovn',
    icon: '🔌',
    kWhUsage: 0.12,
    duration: '5 minutter',
    description:
      'En mikro på 800–1000W forbruker typisk 0.1–0.15 kWh for 5 minutters bruk.',
  },
  {
    id: 'coffee-maker',
    name: 'Kaffetrakter',
    icon: '☕',
    kWhUsage: 0.25,
    duration: 'Per kanne',
    description:
      'En vanlig kaffetrakter bruker ca. 0.2–0.3 kWh for en full kanne.',
  },
  {
    id: 'tv',
    name: 'TV',
    icon: '📺',
    kWhUsage: 0.3,
    duration: '3 timer',
    description:
      'En moderne LED/LCD-TV (80–120W) bruker rundt 0.3 kWh på 3 timers bruk.',
  },
  {
    id: 'gaming-console',
    name: 'Spillkonsoll',
    icon: '🎮',
    kWhUsage: 0.4,
    duration: '3 timer',
    description:
      'En PS5/Xbox Series X trekker 120–200W under spilling, ~0.36–0.6 kWh på 3 timer.',
  },
  {
    id: 'pc',
    name: 'PC/Laptop',
    icon: '💻',
    kWhUsage: 0.5,
    duration: '5 timer',
    description:
      'En stasjonær PC (80–150W) bruker 0.4–0.75 kWh på 5 timer; en laptop trekker mindre.',
  },
  {
    id: 'lighting',
    name: 'Belysning',
    icon: '💡',
    kWhUsage: 0.25,
    duration: '5 timer (hele huset)',
    description:
      'LED-pærer (7–10W) i et snitthjem (~10 stk.) kan bruke 0.2–0.3 kWh på 5 timers belysning.',
  },
  {
    id: 'phone-charging',
    name: 'Lade mobiltelefon',
    icon: '📱',
    kWhUsage: 0.01,
    duration: 'Full lading',
    description:
      'En smarttelefon bruker ca. 5–15 Wh (0.005–0.015 kWh) for en full lading.',
  }
];

const ElectricityUsageCalculator: React.FC<ElectricityCostListProps> = ({
  currentPrice,
  priceAfterSubsidy,
  prices,
  area
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);
  
  // Find current and next hour prices
  const now = new Date();
  const currentHourIndex = prices.findIndex(
    (price) => parseISO(price.time_start) <= now && parseISO(price.time_end) > now
  );
  
  const nextHourPrice = prices[currentHourIndex + 1];
  const nextHourSubsidy = nextHourPrice 
    ? calculateElectricitySubsidy(nextHourPrice.NOK_per_kWh, area) 
    : null;
  
  const priceChange = nextHourPrice && currentPrice
    ? ((nextHourPrice.NOK_per_kWh - currentPrice) / currentPrice) * 100
    : null;
  
  // Listen for navigation events
  useEffect(() => {
    const handleCalculatorNavigated = () => {
      setIsExpanded(true);
    };

    document.addEventListener('calculatorNavigated', handleCalculatorNavigated);
    return () => {
      document.removeEventListener('calculatorNavigated', handleCalculatorNavigated);
    };
  }, []);

  // Decide which price to use
  const priceToUse =
    priceAfterSubsidy !== null ? priceAfterSubsidy : currentPrice;
  const hasSubsidy =
    priceAfterSubsidy !== null && priceAfterSubsidy !== currentPrice;

  // Toggle collapse
  const handleToggle = () => setIsExpanded(!isExpanded);

  // Auto-expand when navigated to via header link (URL hash)
  useEffect(() => {
    // Check if the hash matches our ID
    const checkHash = () => {
      if (window.location.hash === '#calculator') {
        setIsExpanded(true);
      }
    };

    // Check hash on component mount
    checkHash();

    // Listen for hash changes
    window.addEventListener('hashchange', checkHash);

    // Also check the hash after a short delay to handle SPA routing
    const timeoutId = setTimeout(checkHash, 100);

    // Create an intersection observer to check if element is in view
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // If element is scrolled into view and has the matching ID in hash
          if (entry.isIntersecting && window.location.hash === '#calculator') {
            setIsExpanded(true);
          }
        });
      },
      { threshold: 0.1 } // Trigger when 10% of element is visible
    );

    // Start observing our component
    if (componentRef.current) {
      observer.observe(componentRef.current);
    }

    // Explicitly check for clicks on links targeting this component
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const closestLink = target.closest('a');
      
      if (closestLink && closestLink.getAttribute('href')?.includes('#calculator')) {
        setIsExpanded(true);
      }
    };

    document.addEventListener('click', handleClick);

    // Cleanup
    return () => {
      window.removeEventListener('hashchange', checkHash);
      clearTimeout(timeoutId);
      observer.disconnect();
      document.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm overflow-hidden mb-6 dark:border dark:border-dark-border" ref={componentRef} id="calculator">
      {/* Calculator header */}
      <div className="grid grid-cols-1 gap-6 p-4 sm:p-6">
        {/* Quick Calculator */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 sm:p-6 rounded-lg border border-blue-100 dark:border-blue-800">
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Hurtigkalkulator</h4>
          <div className="grid grid-cols-1 gap-4 sm:gap-6">
            {/* Monthly Cost Calculator */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Beregn månedskostnad
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-base"
                  placeholder="kWh per måned"
                  onChange={(e) => {
                    const kWh = parseFloat(e.target.value);
                    const cost = document.getElementById('monthly-cost');
                    if (cost && !isNaN(kWh)) {
                      cost.textContent = (kWh * priceToUse).toFixed(0);
                    } else {
                      cost.textContent = '0';
                    }
                  }}
                />
                <div className="text-right whitespace-nowrap">
                  = <span id="monthly-cost" className="font-bold text-blue-800 dark:text-blue-400">0</span> kr
                </div>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Gjennomsnittlig forbruk: Leilighet ~800 kWh, Enebolig ~2000 kWh
              </p>
            </div>
            
            {/* Power to Cost Calculator */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Beregn timekostnad for apparat
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  placeholder="Watt"
                  onChange={(e) => {
                    const watts = parseFloat(e.target.value);
                    const cost = document.getElementById('hourly-cost');
                    if (cost && !isNaN(watts)) {
                      cost.textContent = ((watts / 1000) * priceToUse).toFixed(2);
                    } else {
                      cost.textContent = '0.00';
                    }
                  }}
                />
                <div className="text-right whitespace-nowrap">
                  = <span id="hourly-cost" className="font-bold text-blue-800 dark:text-blue-400">0.00</span> kr/t
                </div>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Typiske effekter: Panelovn 1000W, LED-pære 10W, Vaskemaskin 2000W
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Cards */}
      {priceToUse && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {usageExamples.map((item) => {
            const cost = (item.kWhUsage * priceToUse).toFixed(2);

            return (
              <div
                key={item.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 hover:shadow-md transition-shadow"
              >
                <div className="p-4">
                  {/* Top row: icon, name, cost */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex">
                      <span className="text-2xl mr-3">{item.icon}</span>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">
                          {item.name}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {item.duration}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-blue-800 dark:text-blue-400 text-xl font-bold">
                        {cost} kr
                      </div>
                      {nextHourPrice && (() => {
                        const currentCost = item.kWhUsage * priceToUse;
                        const nextHourCost = item.kWhUsage * (
                          nextHourSubsidy?.isSubsidyActive 
                            ? nextHourSubsidy.priceAfterSubsidy 
                            : nextHourPrice.NOK_per_kWh
                        );
                        const costDiff = nextHourCost - currentCost;
                        const percentChange = (costDiff / currentCost) * 100;
                        
                        return (
                        <div className="flex items-center justify-end">
                          <div className={`text-sm font-medium ${
                            costDiff > 0 
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-green-600 dark:text-green-400'
                          }`}>
                            Neste time: {nextHourCost.toFixed(2)} kr
                          </div>
                        </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Description & usage info */}
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    {item.description}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    <span className="font-medium text-gray-700 dark:text-gray-200">Forbruk:</span>{' '}
                    {item.kWhUsage.toFixed(2)} kWh
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ElectricityUsageCalculator